const defaultRetryDelay = attempt => Math.min(1000 * Math.pow(2, attempt), 8000)
const defaultTimeout = 30000 // 30 seconds — LLM calls are now async via job polling

export function createHttpClient({ baseUrl = '', headers = {}, retries = 0, retryDelay = defaultRetryDelay, timeout = defaultTimeout } = {}) {
  let currentBaseUrl = baseUrl
  let currentHeaders = { ...headers }

  const shouldRetry = (err, res, attempt) => {
    if (attempt >= retries) return false
    if (err) return true
    if (!res) return false
    if (res.status >= 500) return true
    return false
  }

  const sleep = ms => new Promise(r => setTimeout(r, ms))

  const request = async (path, { method = 'GET', headers: h = {}, body } = {}) => {
    let attempt = 0
    while (true) {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)

      try {
        const res = await fetch(`${currentBaseUrl}${path}`, {
          method,
          headers: { 'Content-Type': 'application/json', ...currentHeaders, ...h },
          body: body === undefined ? undefined : typeof body === 'string' ? body : JSON.stringify(body),
          signal: controller.signal,
        })
        clearTimeout(timeoutId)

        const contentType = res.headers.get('content-type') || ''
        const isJson = contentType.includes('application/json')
        const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '')

        if (!res.ok) {
          const error = new Error(`HTTP ${res.status}`)
          error.status = res.status
          error.body = payload
          if (shouldRetry(null, res, attempt)) {
            await sleep(retryDelay(attempt))
            attempt += 1
            continue
          }
          throw error
        }
        return payload
      } catch (e) {
        clearTimeout(timeoutId)

        if (e.name === 'AbortError') {
          const error = new Error(`Request timeout after ${timeout}ms`)
          error.code = 'TIMEOUT'
          throw error
        }

        if (shouldRetry(e, null, attempt)) {
          await sleep(retryDelay(attempt))
          attempt += 1
          continue
        }

        throw e
      }
    }
  }

  const get = (path, options) => request(path, { ...(options || {}), method: 'GET' })
  const post = (path, body, options) => request(path, { ...(options || {}), method: 'POST', body })
  const setHeader = (k, v) => { currentHeaders[k] = v }
  const setBaseUrl = v => { currentBaseUrl = v }

  return { get, post, setHeader, setBaseUrl }
}
